import React, { useState, useEffect, useCallback } from "react";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import debounce from "lodash/debounce";
import DeleteIcon from "@mui/icons-material/Delete";
import DeactivateIcon from "@mui/icons-material/Block";
import {
  useDeactivateUsersDoctor,
  useDeleteUsersDoctor,
  useGetAdmins,
} from "../../api/ekoApi";
import { useNavigate } from "react-router-dom";
import { SearchInput } from "../../components/SearchInput";
import styled from "styled-components";
import { AdminTabs } from "../../components/AdminTabs";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";

const TabAndSearchWrapper = styled(Box)`
  display: flex;
  justify-content: space-between;
  padding-top: 12px;
  padding-right: 10px;
  align-items: center;
`;

const AdminList = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const { t } = useTranslation();
  const navigate = useNavigate();
  
  const { data, isLoading, isError, refetch } = useGetAdmins(searchTerm);
  const [currentTab, setCurrentTab] = useState(0);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [activateModalOpen, setActivateModalOpen] = useState(false);
  const [deactivateModalOpen, setDeactivateModalOpen] = useState(false);
  const [selectedRowData, setSelectedRowData] = useState(null);
  const { mutate } = useDeleteUsersDoctor();
  const isActive = useDeactivateUsersDoctor();
  
  useEffect(() => {
    if (currentTab === 1) {
      navigate("/alluserstable");
    }
    if (currentTab === 2) {
      navigate("/allpatientstable");
    }
    if (currentTab === 0) {
      navigate("/admintable");
    }
  }, [currentTab, navigate]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleSearch = useCallback(
    debounce((value, refetch) => {
        refetch({ searchTerm: value });
    }, 400),
    []
);

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
  };

  const handleDelete = (rowData) => {
    setDeleteModalOpen(true);
    setSelectedRowData(rowData);
  };

  const handleDeactivate = (rowData) => {
    setDeactivateModalOpen(true);
    setSelectedRowData(rowData);
  };

  const handleConfirmDelete = () => {
    mutate(selectedRowData._id);
    handleCloseDeleteModal();
  };

  const handleCloseDeleteModal = () => {
    setDeleteModalOpen(false);
    setSelectedRowData(null);
  };

  const handleConfirmActivate = () => {
    isActive.mutate({ id: selectedRowData._id, isActive: true });
    handleCloseActivateModal();
  };

  const handleCloseDeactivateModal = () => {
    setDeactivateModalOpen(false);
    setSelectedRowData(null);
  };

  function handleActivate(rowData) {
    setActivateModalOpen(true);
    setSelectedRowData(rowData);
  }

  const handleCloseActivateModal = () => {
    setActivateModalOpen(false);
    setSelectedRowData(null);
  };

  const handleConfirmDeactivate = () => {
    isActive.mutate({ id: selectedRowData._id, isActive: false });
    handleCloseDeactivateModal();
  };

  const columnDefsAdmins = [
    { field: "displayName", headerName: t("displayName"), flex: 1 , sortable: true },
    { field: "email", headerName: t("emailLabel"), flex: 1, sortable: true },
    { field: "organization", headerName: t("organization"), flex: 1, sortable: true },
    { field: "isActive", headerName: t("active"), sortable: true },
    {
      headerName: t("actions"),
      cellRenderer: (params) => (
        <>
          <DeleteIcon
            onClick={() => handleDelete(params.data)}
            style={{ cursor: "pointer" }}
          />
          {params?.data?.isActive && (
            <DeactivateIcon
              onClick={() => handleDeactivate(params?.data)}
              style={{
                cursor: "pointer",
                color: "#ff7b7b",
                marginLeft: "10px",
              }}
            />
          )}
          {!params?.data?.isActive && (
            <CheckCircleOutlineIcon
              onClick={() => handleActivate(params?.data)}
              style={{ cursor: "pointer", color: "green", marginLeft: "10px" }}
            />
          )}
        </>
      ),
    },
  ];

  if (isLoading) {
    return <div>Loading...</div>;
  }

  if (isError) {
    return <div>Error loading admin data: {isError.message}</div>;
  }

  

  const handleSearchInput = (e) => {
    const value = e.target.value;
    setSearchTerm(value);

    handleSearch(value, refetch);
  };

  const rowData = data?.admins || [];

  return (
    <div>
      <TabAndSearchWrapper>
        <AdminTabs currentTab={currentTab} handleTabChange={handleTabChange} />
        <SearchInput
          searchText={searchTerm}
          handleSearchInputChange={handleSearchInput}
        />
      </TabAndSearchWrapper>
      <div
        className="ag-theme-alpine"
        style={{ height: "80vh", width: "100%" }}
      >
        <AgGridReact
          rowData={rowData}
          columnDefs={columnDefsAdmins}
          localeText={{noRowsToShow: t('noRowsToShow')}}
        ></AgGridReact>
      </div>

      {/* Delete Modal */}
      <Dialog open={deleteModalOpen} onClose={handleCloseDeleteModal}>
        <DialogTitle>{t("deleteConfirmation")}</DialogTitle>
        <DialogContent>
          {t("areYouSureToDelete")}{" "}
          {selectedRowData && selectedRowData.username}?
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteModal}>{t("cancel")}</Button>
          <Button onClick={handleConfirmDelete} color="error">
            {t("delete")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Deactivate Modal */}
      <Dialog open={deactivateModalOpen} onClose={handleCloseDeactivateModal}>
        <DialogTitle>{t("deactivateConfirmation")}</DialogTitle>
        <DialogContent>
          {t("areYouSureDeactivate")}{" "}
          {selectedRowData && selectedRowData.username}?
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeactivateModal}>{t("cancel")}</Button>
          <Button onClick={handleConfirmDeactivate}>{t("deactivate")}</Button>
        </DialogActions>
      </Dialog>
      <Dialog open={activateModalOpen} onClose={handleCloseDeactivateModal}>
        <DialogTitle>{t("activateUser")}</DialogTitle>
        <DialogContent>
          {t("areYouSureActivate")}{" "}
          {selectedRowData && selectedRowData.username}?
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseActivateModal}>{t("cancel")}</Button>
          <Button onClick={handleConfirmActivate}>{t("activate")}</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default AdminList;
